import { type Node, useStore, useViewport } from "@xyflow/react"
import { Bot, Search, Sparkles } from "lucide-react"
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { cn } from "@/lib/utils"
import type { ProjectNodeData } from "@/types/project"
import { filterBuiltInNodeTemplates } from "./built-in-nodes"
import { PaletteNodeActions } from "./palette-node-actions"
import { getNodesBounds, PALETTE_VERTICAL_OFFSET } from "./positioning"
import type { BuiltInNodeTemplate, PaletteState } from "./types"

const PALETTE_WIDTH = 360
const PALETTE_EXIT_DURATION_MS = 100
const VIEWPORT_PADDING = 12

const clamp = (value: number, min: number, max: number) => {
	if (max < min) return min
	return Math.min(max, Math.max(min, value))
}

const fitAxis = ({
	preferred,
	fallback,
	size,
	available,
}: {
	preferred: number
	fallback: number
	size: number
	available: number
}) => {
	const max = Math.max(VIEWPORT_PADDING, available - size - VIEWPORT_PADDING)
	if (
		preferred >= VIEWPORT_PADDING &&
		preferred + size <= available - VIEWPORT_PADDING
	) {
		return preferred
	}
	if (
		fallback >= VIEWPORT_PADDING &&
		fallback + size <= available - VIEWPORT_PADDING
	) {
		return fallback
	}
	return clamp(preferred, VIEWPORT_PADDING, max)
}

export const CommandPalette = ({
	state,
	selectedNodes,
	onClose,
	onCreateNode,
	onDeleteNodes,
}: {
	state: PaletteState
	selectedNodes: Node<ProjectNodeData>[]
	onClose: () => void
	onCreateNode: (template: BuiltInNodeTemplate) => void
	onDeleteNodes: (nodeIds: string[]) => void
}) => {
	const [query, setQuery] = useState("")
	const [selectedIndex, setSelectedIndex] = useState(0)
	const [renderedState, setRenderedState] = useState<PaletteState>(state)
	const [isExiting, setIsExiting] = useState(false)
	const [paletteSize, setPaletteSize] = useState({
		width: PALETTE_WIDTH,
		height: 0,
	})
	const inputRef = useRef<HTMLInputElement>(null)
	const paletteRef = useRef<HTMLDivElement>(null)
	const lastPositionRef = useRef<{ left: number; top: number } | null>(null)
	const viewport = useViewport()
	const paneWidth = useStore((store) => store.width)
	const paneHeight = useStore((store) => store.height)
	const filteredTemplates = useMemo(
		() => filterBuiltInNodeTemplates(query),
		[query],
	)
	const draftPosition = state.mode === "create" ? state.draftPosition : null
	const visibleState = state.mode === "closed" ? renderedState : state

	useLayoutEffect(() => {
		if (state.mode !== "closed") {
			setRenderedState(state)
			setIsExiting(false)
			return
		}
		if (renderedState.mode === "closed") return

		setIsExiting(true)
		const exitDuration = window.matchMedia("(prefers-reduced-motion: reduce)")
			.matches
			? 0
			: PALETTE_EXIT_DURATION_MS
		const timeout = window.setTimeout(() => {
			setRenderedState({ mode: "closed" })
			setIsExiting(false)
		}, exitDuration)
		return () => window.clearTimeout(timeout)
	}, [renderedState, state])

	useLayoutEffect(() => {
		if (!draftPosition) return
		setQuery("")
		setSelectedIndex(0)
	}, [draftPosition])

	useEffect(() => {
		if (!draftPosition) return
		const timeout = window.setTimeout(() => inputRef.current?.focus(), 0)
		return () => window.clearTimeout(timeout)
	}, [draftPosition])

	useEffect(() => {
		setSelectedIndex((current) => {
			if (filteredTemplates.length === 0) return 0
			return Math.min(current, filteredTemplates.length - 1)
		})
	}, [filteredTemplates.length])

	useLayoutEffect(() => {
		if (visibleState.mode === "closed") return
		const palette = paletteRef.current
		if (!palette) return

		const updateSize = () => {
			const bounds = palette.getBoundingClientRect()
			setPaletteSize((current) => {
				if (
					current.width === bounds.width &&
					current.height === bounds.height
				) {
					return current
				}
				return { width: bounds.width, height: bounds.height }
			})
		}
		updateSize()
		const observer = new ResizeObserver(updateSize)
		observer.observe(palette)
		return () => observer.disconnect()
	}, [visibleState.mode])

	if (visibleState.mode === "closed") return null

	const selectionBounds = getNodesBounds(selectedNodes)
	const nextAnchor =
		visibleState.mode === "create"
			? {
					type: "pointer" as const,
					x: visibleState.draftPosition.x * viewport.zoom + viewport.x,
					y: visibleState.draftPosition.y * viewport.zoom + viewport.y,
				}
			: selectionBounds
				? {
						type: "selection" as const,
						x:
							(selectionBounds.x + selectionBounds.width / 2) * viewport.zoom +
							viewport.x,
						top: selectionBounds.y * viewport.zoom + viewport.y,
						bottom:
							(selectionBounds.y + selectionBounds.height) * viewport.zoom +
							viewport.y,
					}
				: null
	const nextPosition = nextAnchor
		? {
				left: fitAxis({
					preferred:
						nextAnchor.type === "pointer"
							? nextAnchor.x
							: nextAnchor.x - paletteSize.width / 2,
					fallback:
						nextAnchor.type === "pointer"
							? nextAnchor.x - paletteSize.width
							: nextAnchor.x - paletteSize.width / 2,
					size: paletteSize.width,
					available: paneWidth,
				}),
				top: fitAxis({
					preferred:
						nextAnchor.type === "pointer"
							? nextAnchor.y
							: nextAnchor.bottom + PALETTE_VERTICAL_OFFSET,
					fallback:
						nextAnchor.type === "pointer"
							? nextAnchor.y - paletteSize.height
							: nextAnchor.top - PALETTE_VERTICAL_OFFSET - paletteSize.height,
					size: paletteSize.height,
					available: paneHeight,
				}),
			}
		: null
	if (nextPosition) lastPositionRef.current = nextPosition
	const position = nextPosition ?? lastPositionRef.current
	if (!position) return null
	const maxPaletteHeight =
		paneHeight > 0 ? Math.max(0, paneHeight - VIEWPORT_PADDING * 2) : undefined
	const submitSelection = (template: BuiltInNodeTemplate) => {
		onCreateNode(template)
		setQuery("")
		setSelectedIndex(0)
	}
	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Escape") {
			event.preventDefault()
			onClose()
			return
		}
		if (event.key === "ArrowDown") {
			event.preventDefault()
			setSelectedIndex((index) =>
				Math.min(filteredTemplates.length - 1, index + 1),
			)
			return
		}
		if (event.key === "ArrowUp") {
			event.preventDefault()
			setSelectedIndex((index) => Math.max(0, index - 1))
			return
		}
		if (event.key === "Enter" && filteredTemplates[selectedIndex]) {
			event.preventDefault()
			submitSelection(filteredTemplates[selectedIndex])
		}
	}

	return (
		<div
			ref={paletteRef}
			className={cn(
				"absolute z-50 w-[360px] max-w-[calc(100%-1.5rem)]",
				isExiting && "pointer-events-none",
			)}
			style={{
				left: position.left,
				maxHeight: maxPaletteHeight,
				top: position.top,
			}}
		>
			<div
				className={cn(
					"flex origin-top flex-col overflow-hidden rounded-xl border border-border/65 bg-background/96 shadow-2xl shadow-black/30 ring-1 ring-foreground/5 motion-reduce:animate-none backdrop-blur-md",
					isExiting
						? "animate-out fade-out-0 zoom-out-95 slide-out-to-top-1 duration-100 ease-in fill-mode-forwards"
						: "animate-in fade-in-0 zoom-in-95 slide-in-from-top-1 duration-50 ease-out",
				)}
				style={{ maxHeight: maxPaletteHeight }}
			>
				{visibleState.mode === "selection" ? (
					<PaletteNodeActions
						selectedNodes={selectedNodes}
						onDeleteNodes={onDeleteNodes}
					/>
				) : (
					<>
						<div className="flex shrink-0 items-center gap-2 border-b border-border/45 px-3 py-2">
							<Sparkles className="size-3.5 text-primary" />
							<p className="text-xs font-medium">Create node</p>
						</div>
						<div className="min-h-0 overflow-y-auto p-3">
							<div className="relative">
								<Search className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
								<input
									ref={inputRef}
									type="text"
									value={query}
									onChange={(event) => setQuery(event.target.value)}
									onKeyDown={handleKeyDown}
									placeholder="Search nodes or describe what you want…"
									className="h-10 w-full rounded-lg border border-border/55 bg-muted/35 pl-9 pr-3 text-sm outline-none placeholder:text-muted-foreground/70 focus:border-primary/55 focus:ring-2 focus:ring-primary/30"
									autoComplete="off"
								/>
							</div>

							<div className="mt-2 max-h-[252px] overflow-y-auto rounded-lg border border-border/50 bg-muted/20 p-1">
								{filteredTemplates.map((template, index) => (
									<button
										key={template.id}
										type="button"
										onMouseEnter={() => setSelectedIndex(index)}
										onClick={() => submitSelection(template)}
										className={cn(
											"w-full rounded-md px-2 py-1.5 text-left transition-colors hover:bg-background/70 focus:outline-none",
											index === selectedIndex && "bg-background/80 shadow-sm",
										)}
									>
										<p className="text-sm font-medium">{template.label}</p>
										<p className="line-clamp-1 text-xs text-muted-foreground">
											{template.description}
										</p>
									</button>
								))}
								{filteredTemplates.length === 0 ? (
									<p className="px-2 py-3 text-center text-xs text-muted-foreground">
										No built-in nodes found
									</p>
								) : null}
							</div>

							{query.trim() ? (
								<div
									aria-disabled="true"
									className="mt-2 flex items-center gap-2 rounded-lg border border-dashed border-primary/30 bg-primary/5 px-3 py-2 opacity-70"
								>
									<Bot className="size-3.5 text-primary" />
									<p className="min-w-0 flex-1 truncate text-xs">
										Ask Origin to create “{query.trim()}”
									</p>
									<span className="text-[10px] uppercase tracking-wide text-muted-foreground">
										Coming soon
									</span>
								</div>
							) : null}
						</div>
						<div className="flex shrink-0 items-center justify-between border-t border-border/35 bg-muted/15 px-3 py-1.5 text-[10px] text-muted-foreground">
							<span>↑↓ Navigate</span>
							<span>Enter Create</span>
							<span>Esc Close</span>
						</div>
					</>
				)}
			</div>
		</div>
	)
}
